import { useState, useRef, useEffect } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot,
  Send,
  StopCircle,
  X,
  Loader2,
  Minimize2,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { getOrRefreshToken } from "@/lib/api";
import { GlassCard } from "@/components/ui";
import { AgentConfirmationCard, extractConfirmation } from "@/components/AgentConfirmationCard";

// ════════════════════════════════════════════════════════════════════════════
// AgentChatBar — Floating chat bubble for all dashboard pages
// ════════════════════════════════════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_API_URL ?? "";

export function AgentChatBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const { messages, sendMessage, status, error, stop } = useChat({
    transport: new DefaultChatTransport({
      api: `${API_BASE}/api/agent/chat`,
      headers: async () => {
        const token = await getOrRefreshToken();
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        return headers;
      },
    }),
  });

  const isBusy = status === "streaming" || status === "submitted";

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Don't show on the /agent full-page route (after hooks)
  if (location.pathname === "/agent") return null;

  const handleSend = () => {
    const text = input.trim();
    if (!text || isBusy) return;
    setInput("");
    if (!isOpen) setIsOpen(true);
    sendMessage({ text });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="w-80 sm:w-96"
          >
            <GlassCard className="flex flex-col overflow-hidden rounded-2xl shadow-xl border border-white/20"
              style={{ height: "420px" }}
            >
              {/* Panel header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                <Bot className="w-4 h-4 text-af-700 dark:text-af-300" />
                <span className="text-sm font-medium text-af-900 dark:text-af-100 flex-1">Flo</span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-af-400 hover:text-af-700 dark:hover:text-af-200 transition-colors"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {messages.length === 0 && (
                  <p className="text-xs text-af-400 text-center mt-4">
                    Ask Flo anything about your business
                  </p>
                )}

                {messages.map((msg) => {
                  const isUser = msg.role === "user";
                  const text = (msg.parts ?? [])
                    .filter((p): p is { type: "text"; text: string } => p.type === "text")
                    .map((p) => p.text)
                    .join("\n\n")
                    .trim();
                  const confirmation = !isUser && msg.parts ? extractConfirmation(msg.parts as unknown[]) : null;
                  if (!text && !confirmation) return null;

                  return (
                    <div key={msg.id} className={`flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`}>
                      {text && (
                        <div
                          className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                            isUser
                              ? "bg-af-900/90 dark:bg-af-100/90 text-white dark:text-af-900 rounded-br-sm"
                              : "bg-white/10 dark:bg-white/5 text-af-900 dark:text-af-100 rounded-bl-sm"
                          }`}
                        >
                          <div className="whitespace-pre-wrap">{text}</div>
                        </div>
                      )}
                      {confirmation && (
                        <AgentConfirmationCard
                          actionId={confirmation.action_id}
                          actionType={confirmation.action_type}
                          preview={confirmation.preview}
                        />
                      )}
                    </div>
                  );
                })}

                {isBusy && (
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 text-af-400 animate-spin" />
                    <span className="text-xs text-af-400">Thinking…</span>
                  </div>
                )}

                {error && (
                  <p className="text-xs text-red-400">{error.message}</p>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="px-3 pb-3 border-t border-white/10 pt-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything…"
                    className="flex-1 bg-white/10 dark:bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-xs text-af-900 dark:text-af-100 placeholder:text-af-400 outline-none focus:border-white/30 transition-colors"
                    disabled={isBusy}
                  />
                  {isBusy ? (
                    <button
                      onClick={stop}
                      className="text-red-400 hover:text-red-500 transition-colors"
                    >
                      <StopCircle className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSend}
                      disabled={!input.trim()}
                      className="text-af-700 dark:text-af-300 hover:text-af-900 dark:hover:text-af-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating bubble (collapsed state) */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(true)}
          className="flex items-center justify-center w-12 h-12 rounded-full bg-af-900 dark:bg-af-100 shadow-lg hover:shadow-xl transition-shadow"
        >
          <Bot className="w-5 h-5 text-white dark:text-af-900" />
        </motion.button>
      )}

      {/* Bubble with close when open */}
      {isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(false)}
          className="flex items-center justify-center w-12 h-12 rounded-full bg-af-900 dark:bg-af-100 shadow-lg hover:shadow-xl transition-shadow"
        >
          <X className="w-5 h-5 text-white dark:text-af-900" />
        </motion.button>
      )}
    </div>
  );
}
